// src\app\services\invitation\invitation.service.ts
import { inject, Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, firstValueFrom, interval, map, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import { UserService } from '../user/user.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { Invitation, InvitationDto, InvitationMapper } from 'src/app/model/Invitation';
import { AbstractApiService } from 'src/app/services/abstract-api-service/abstract-api-service';
import { DateTime } from 'luxon';
import { AuthService } from '../auth/auth.service';
import { User } from 'src/app/model/User';
import {
  SEND_INVITATION_ENDPOINT,
  RESEND_INVITATION_ENDPOINT,
  COMPANY_INVITES_ENDPOINT,
  RESEND_INVITE_MEMBER_ENDPOINT
} from 'src/app/constants/api-routes';

/**
 * InvitationService is used to hold/move invitation data from the backend to the application
 */
@Injectable({
  providedIn: 'root'
})
export class InvitationService extends AbstractApiService<Invitation> {
  /** A list of invitations the user has access to */
  protected _http: HttpClient = inject(HttpClient);
  protected _collection?: Invitation[];
  protected _subject = new Subject<Invitation[]>();
  private _invitationMapper = inject(InvitationMapper);
  public invitations$ = this._subject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  constructor(
    private _userService: UserService,
    private _authService: AuthService,
  ) {
    super();
    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._collection = undefined;
        this._subject.next([]);
      }
    });

    // Continuously query the backend to receive new applications
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetch();
      });
  }

  /**
   * Get a list of invitations
   * 
   * @return A reference to invitations the user has access to
   */
  public get(): Invitation[] | undefined {
    return this._collection;
  }

  /**
   * Set a list of invitations
   * 
   * @param invitations The list of invitations to set
   */
  private _set(invitations: Invitation[]): void {
    this._collection = invitations.map(x => x.copyOf());
    this._subject.next(invitations.map(x => x.copyOf()));
  }

  /**
   * Send a new application to the backend
   *
   * @param merchandId Merchant ID to send this application from
   * @param invitation The application to send
   * @returns observable when the application is sent
   */
  public sendInvitation(invitation: Invitation): Observable<Invitation> {
    const company = this._userService.company;
    if (company == undefined) throw new Error('No company found for user');

    return this._http.post<InvitationDto>(SEND_INVITATION_ENDPOINT(company.companyId), this._invitationMapper.toDto(invitation)).pipe(
      map((dto: InvitationDto) => {
        const inv = this._invitationMapper.toModel(dto);
        this.refresh(inv, 'create');
        return inv;
      }),
    );
  }

  /**
   * Calls the backend resend invitation endpoint for the given token.
   * @param token The invitation token
   * @returns an Observable with the updated Invitation data from the backend.
   */
  public async resendInvitation(invitation: Invitation): Promise<string> {
    // Get company ID from the user service
    const companyId = this._userService.company?.companyId;
    if (companyId == undefined) {
      return Promise.reject('No supplier ID found');
    }

    // Create request body
    const requestBody = {
      email: invitation.email,
    };

    // Send request
    const response = await firstValueFrom(this._http.post<{ message: string }>(RESEND_INVITATION_ENDPOINT(companyId), requestBody)).then(res => res.message);
    this.refresh(Object.assign(invitation, { lastSent: DateTime.now().toFormat(AppConstants.DATE_FORMAT) }), 'modify');
    return response;
  }

  // src/app/services/invitation/invitation.service.ts
  public async resendInvitationMember(invitation: Invitation): Promise<string> {
    const companyId = this._userService.company?.companyId;
    if (companyId == null) return Promise.reject('No supplier ID found');

    const email = (invitation.email || '').trim();
    if (!email) return Promise.reject('Missing email address');

    try {
      const body = { email };
      const res = await firstValueFrom(
        this._http.post<{ message: string }>(RESEND_INVITE_MEMBER_ENDPOINT(companyId), body)
      );
      // Update local cache timestamp for UI
      this.refresh(
        Object.assign(invitation, { lastSent: DateTime.now().toFormat(AppConstants.DATE_FORMAT) }),
        'modify'
      );
      return res?.message || 'Invite resent.';
    } catch (err: any) {
      // Let caller inspect status/message (PeoplePage already does)
      throw err;
    }
  }

  /**
   * Get invitations from the backend. You should subscribe to the 'invitations$'
   * field to receive updates
   * 
   * @note This method could return early if the user is a borrower/supplier by calling the
   * user service (ex: `this._userService.userRole === 'borrower'`)
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user's active company
    const company = this._userService.company;
    if (company == undefined) return;

    // Query the backend for invitations
    this._http.get<InvitationDto[]>(COMPANY_INVITES_ENDPOINT(company.companyId)).subscribe({
      next: (interfaces: InvitationDto[]): void => {
        if (interfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output of invitations into list`);
        }

        // Convert the interfaces to class values
        let invitations: Invitation[] = [];
        for (let invInt of interfaces) {
          invitations.push(this._invitationMapper.toModel(invInt));
        }

        // Propagate changes if different
        console.log(`All Invites: ${invitations}`);
        invitations = invitations.filter(inv => inv.accepted === 0);
        console.log(`Filtered Invites: ${invitations}`);
        this._set(invitations);
        console.log(`[INFO]: successfully queried invitations`);
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${("" + seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }

}

// Helper function to delay for a given number of milliseconds
const delay = (delay: number): Promise<void> => {
  return new Promise(r => { setTimeout(r, delay) });
}
